// /scada/reports — reports catalogue + sample preview (R4). Mock numbers.
import { useMemo, useState } from 'react';
import ScadaLayout from '@/components/feature/ScadaLayout';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { STATIONS } from '@/scada/mock/stations';
import { useAllowedStationIds } from '@/scada/useScopedStations';
import { snapshotFleet } from '@/scada/mock/telemetry';

interface ReportDef { id: string; title: string; desc: string; period: string; formats: string[]; preview: boolean; }
const REPORTS: ReportDef[] = [
  { id: 'daily', title: 'Оперативний добовий', desc: 'Генерація, аварії, обмеження, команди, звʼязок, невирішені питання', period: 'Доба', formats: ['Web', 'PDF', 'Excel'], preview: true },
  { id: 'monthly', title: 'Місячний сервісний', desc: 'План, факт, виконання, події, втрати, статистика обладнання, висновок', period: 'Місяць', formats: ['Word', 'PDF', 'Excel'], preview: true },
  { id: 'losses', title: 'Втрати та небаланси', desc: 'Небаланси, втрати від обладнання, СКО, операційної безпеки, обмежень, УЗЕ', period: 'Будь-який', formats: ['Excel', 'CSV'], preview: false },
  { id: 'sko', title: 'СКО та обмеження', desc: 'Команди, підтвердження, виконання, відбій, недовиробіток, компенсація', period: 'Доба / місяць', formats: ['PDF', 'Excel'], preview: false },
  { id: 'avail', title: 'Доступність обладнання', desc: 'Коефіцієнт доступності, простої, статистика виходів обладнання', period: 'Місяць', formats: ['PDF', 'Excel'], preview: false },
  { id: 'exec', title: 'Звіт для керівника', desc: 'Стисла картина: генерація, втрати, топ проблем, виконання прогнозу, ефект', period: 'Тиждень / місяць', formats: ['PDF'], preview: false },
];

export default function ScadaReportsPage() {
  const [sel, setSel] = useState<string | null>(null);
  const fleet = useMemo(() => snapshotFleet(), []);
  const allowed = useAllowedStationIds();

  const rows = STATIONS.filter((s) => !allowed || allowed.has(s.id)).map((s) => {
    const t = fleet.find((f) => f.stationId === s.id);
    const fact = t ? t.dailyEnergyKwh / 1000 : 0;
    const plan = +(s.acCapacityKw * 4.9 / 1000).toFixed(1);
    const done = plan ? Math.round((fact / plan) * 100) : 0;
    return { name: s.name, plan, fact: +fact.toFixed(1), done, limited: !!t?.limitReason, offline: t?.state === 'offline' };
  });
  const totPlan = rows.reduce((a, r) => a + r.plan, 0);
  const totFact = rows.reduce((a, r) => a + r.fact, 0);

  const selDef = REPORTS.find((r) => r.id === sel);

  return (
    <ScadaLayout active="reports">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" style={{ marginBottom: sel ? 16 : 0 }}>
        {REPORTS.map((r) => (
          <div key={r.id} style={{ background: scada.panel, border: `1px solid ${sel === r.id ? scada.accent + '80' : scada.border}`, borderRadius: 12, padding: 14 }}>
            <div style={{ color: scada.text, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{r.title}</div>
            <div style={{ color: scada.textMuted, fontSize: 12, marginBottom: 8, minHeight: 48 }}>{r.desc}</div>
            <div className="flex items-center gap-1 flex-wrap" style={{ marginBottom: 10 }}>
              <span style={{ color: scada.textDim, fontSize: 11, marginRight: 4 }}>{r.period}</span>
              {r.formats.map((f) => (
                <span key={f} style={{ background: scada.panel2, color: scada.textDim, border: `1px solid ${scada.border}`, borderRadius: 4, padding: '1px 6px', fontSize: 10 }}>{f}</span>
              ))}
            </div>
            <button onClick={() => setSel(r.id)} style={{ background: scada.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', width: '100%' }}>
              Сформувати
            </button>
          </div>
        ))}
      </div>

      {selDef && selDef.preview && (
        <SectionCard
          title={`${selDef.title} — попередній перегляд`}
          subtitle={`${new Date().toLocaleDateString('uk-UA')} · демонстраційні дані`}
          right={<div className="flex gap-1">{selDef.formats.map((f) => <button key={f} style={{ background: scada.panel2, color: scada.textDim, border: `1px solid ${scada.border}`, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}><i className="ri-download-line" /> {f}</button>)}</div>}
          padded={false}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: scada.panel2 }}>
                {['Станція', 'План, МВт·год', 'Факт, МВт·год', 'Виконання', 'Примітки'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', color: scada.textMuted, fontSize: 11, fontWeight: 600, padding: '8px 12px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} style={{ borderTop: `1px solid ${scada.border}` }}>
                  <td style={{ padding: '8px 12px', color: scada.text, fontSize: 13 }}>{r.name}</td>
                  <td style={{ padding: '8px 12px', color: scada.textDim, fontSize: 13 }}>{r.plan}</td>
                  <td style={{ padding: '8px 12px', color: scada.text, fontSize: 13 }}>{r.fact}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{ color: r.done >= 90 ? scada.state.ok : r.done >= 60 ? scada.state.warning : scada.state.alarm, fontSize: 13, fontWeight: 600 }}>{r.done}%</span>
                  </td>
                  <td style={{ padding: '8px 12px', color: scada.textMuted, fontSize: 12 }}>
                    {r.offline ? 'втрата звʼязку' : r.limited ? 'діяло обмеження' : '—'}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: `2px solid ${scada.borderStrong}`, background: scada.panel2 }}>
                <td style={{ padding: '8px 12px', color: scada.text, fontSize: 13, fontWeight: 700 }}>Разом</td>
                <td style={{ padding: '8px 12px', color: scada.textDim, fontSize: 13, fontWeight: 700 }}>{totPlan.toFixed(1)}</td>
                <td style={{ padding: '8px 12px', color: scada.text, fontSize: 13, fontWeight: 700 }}>{totFact.toFixed(1)}</td>
                <td style={{ padding: '8px 12px', color: scada.state.ok, fontSize: 13, fontWeight: 700 }}>{totPlan ? Math.round((totFact / totPlan) * 100) : 0}%</td>
                <td style={{ padding: '8px 12px' }}></td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      )}

      {selDef && !selDef.preview && (
        <SectionCard title={`${selDef.title}`} subtitle="Звіт сформовано (демонстрація)">
          <div className="flex items-center gap-3">
            <i className="ri-file-chart-line" style={{ color: scada.state.ok, fontSize: 28 }} />
            <div style={{ flex: 1 }}>
              <div style={{ color: scada.text, fontSize: 13 }}>{selDef.desc}</div>
              <div style={{ color: scada.textMuted, fontSize: 12 }}>Період: {selDef.period}</div>
            </div>
            <div className="flex gap-1">
              {selDef.formats.map((f) => <button key={f} style={{ background: scada.accent, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}><i className="ri-download-line" /> {f}</button>)}
            </div>
          </div>
        </SectionCard>
      )}
    </ScadaLayout>
  );
}
